import { createTheme, Theme } from '@mui/material/styles';

/**
 * The Tasks page look, for MUI pages: Inter, normal weight, medium (500) for emphasis,
 * 14px everywhere, blue as the one accent, and hover shown as a
 * blue outline or tint, never a shadow. Wrap a page in
 * <ThemeProvider theme={tasksTheme}> to give it this look; the menus, dropdowns
 * and popups it opens pick it up too.
 *
 * Colours are the Tasks page's own (public/tasks-v2/index.html :root).
 */
const C = {
  bg: '#f3f5fb', card: '#fff', line: '#e6e9f2', text: '#1b2236', muted: '#6f7890',
  primary: '#2b6cf6', primaryDark: '#1f57d1', primarySoft: '#e8f0fe',
  danger: '#e0353f', dangerSoft: '#fde8ea', ok: '#1d9a4f', okSoft: '#e2f7ea', warn: '#c27a10', warnSoft: '#fff3d3',
  shadow: '0 4px 18px rgba(20,30,60,.07)', pop: '0 12px 30px rgba(20,30,60,.16)',
};
export const TASKS_COLORS = C;
export const TASKS_FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

const tasksTheme = createTheme({
  palette: {
    primary: { main: C.primary, dark: C.primaryDark, light: C.primarySoft, contrastText: '#fff' },
    error: { main: C.danger, light: C.dangerSoft },
    success: { main: C.ok, light: C.okSoft },
    warning: { main: C.warn, light: C.warnSoft },
    text: { primary: C.text, secondary: C.muted },
    background: { default: C.bg, paper: C.card },
    divider: C.line,
    action: { hover: C.primarySoft, selected: C.primarySoft },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: TASKS_FONT,
    fontSize: 14,
    htmlFontSize: 16,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 500,
    h4: { fontSize: 14, fontWeight: 500 },
    h5: { fontSize: 14, fontWeight: 500 },
    h6: { fontSize: 14, fontWeight: 500 },
    subtitle1: { fontSize: 14, fontWeight: 500 },
    subtitle2: { fontSize: 14, fontWeight: 500 },
    body1: { fontSize: 14 },
    body2: { fontSize: 14 },
    caption: { fontSize: 14 },
    overline: { fontSize: 14, fontWeight: 500, letterSpacing: 0.6 },
    button: { fontSize: 14, fontWeight: 500, textTransform: 'none' },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 14 },
        elevation1: { boxShadow: C.shadow },
        elevation2: { boxShadow: C.shadow },
        elevation3: { boxShadow: C.shadow },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 11, fontWeight: 500, textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        outlined: {
          borderWidth: 2, borderColor: C.line, color: C.text, background: C.card,
          '&:hover': { borderWidth: 2, borderColor: C.primary, background: C.card },
        },
        contained: { '&:hover': { background: C.primaryDark } },
        text: { '&:hover': { background: C.primarySoft } },
        sizeSmall: { fontSize: 14, padding: '4px 12px' },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10, '&:hover': { background: C.primarySoft, color: C.primary } } },
    },
    // Toggle groups look like the Tasks tab groups: grey tray, white chosen tab.
    MuiToggleButtonGroup: {
      styleOverrides: { root: { background: '#eef0f6', borderRadius: 12, padding: 3, gap: 2 }, grouped: { border: 0, borderRadius: '9px !important', margin: 0 } },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: 0, borderRadius: 9, padding: '4px 12px', fontSize: 14, fontWeight: 500, textTransform: 'none', color: C.muted,
          '&:hover': { background: 'transparent', outline: `2px solid ${C.primary}`, outlineOffset: -2 },
          '&.Mui-selected, &.Mui-selected:hover': { background: '#fff', color: C.primary, boxShadow: '0 1px 4px rgba(20,30,60,.12)' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { height: 3, borderRadius: 3, background: C.primary } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontSize: 14, fontWeight: 500, color: C.muted, minHeight: 44, '&:hover': { color: C.primary }, '&.Mui-selected': { color: C.primary } } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12, background: '#fff', fontSize: 14,
          '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: C.line },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.primary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: C.primary },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: 14, fontWeight: 400, color: C.muted } } },
    // Dropdown menus: white card, blue tint on the row under the pointer.
    MuiPopover: { styleOverrides: { paper: { borderRadius: 12, border: `2px solid ${C.line}`, boxShadow: C.pop } } },
    MuiMenu: { styleOverrides: { list: { padding: 4 } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8, fontSize: 14, fontWeight: 400, minHeight: 36,
          '&:hover': { background: C.primarySoft },
          '&.Mui-selected, &.Mui-selected:hover': { background: C.primarySoft, color: C.primary, fontWeight: 500 },
        },
      },
    },
    // Popups: centred, rounded, over a dimmed page.
    MuiDialog: { styleOverrides: { paper: { borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,.3)' } } },
    MuiBackdrop: { styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(20,30,60,.45)' } } } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: 14, fontWeight: 500 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 14, borderColor: C.line, paddingLeft: 12, paddingRight: 12 },
        head: { fontSize: 14, fontWeight: 500, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, background: '#fff', whiteSpace: 'nowrap' },
      },
    },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { background: C.bg } } } },
    MuiChip: { styleOverrides: { root: { borderRadius: 99, fontWeight: 500, fontSize: 14 } } },
    MuiTooltip: { styleOverrides: { tooltip: { background: C.text, borderRadius: 8, fontSize: 14, fontWeight: 400 } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 12, fontSize: 14 } } },
    MuiSwitch: { styleOverrides: { switchBase: { '&:hover': { background: 'transparent' } } } },
    MuiFormControlLabel: { styleOverrides: { label: { fontSize: 14, fontWeight: 400 } } },
  },
});

/**
 * Only the Tasks type (Inter, 14px everywhere, normal weight with medium for
 * emphasis) laid over a page's own colours and shapes. For pages that keep their
 * look but should read like Tasks: <ThemeProvider theme={tasksTypeOnly}>.
 */
let typeOnly: Theme | undefined;
export const tasksTypeOnly = (outer: Theme): Theme => {
  if (typeOnly) return typeOnly;
  const size = { fontSize: 14 };
  const head = { fontSize: 14, fontWeight: 500 };
  typeOnly = createTheme({
    palette: outer.palette,
    typography: {
      fontFamily: TASKS_FONT,
      fontSize: 14,
      fontWeightLight: 400, fontWeightRegular: 400, fontWeightMedium: 500, fontWeightBold: 500,
      h1: head, h2: head, h3: head, h4: head, h5: head, h6: head,
      subtitle1: head, subtitle2: head,
      body1: size, body2: size, caption: size,
      overline: { ...head, letterSpacing: 0.6 },
      button: { ...head, textTransform: 'none' },
    },
    components: {
      MuiButton: { styleOverrides: { sizeSmall: size, sizeLarge: size } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontSize: 14, fontWeight: 500 } } },
      MuiChip: { styleOverrides: { root: size } },
      MuiTooltip: { styleOverrides: { tooltip: { fontSize: 14, fontWeight: 400 } } },
      MuiInputBase: { styleOverrides: { root: size } },
      MuiInputLabel: { styleOverrides: { root: size } },
      MuiMenuItem: { styleOverrides: { root: size } },
      MuiTableCell: { styleOverrides: { root: size, head: { fontWeight: 500 } } },
      MuiBadge: { styleOverrides: { badge: { fontWeight: 500 } } },
    },
  });
  return typeOnly;
};

export default tasksTheme;
